import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Eye, CheckCircle2, FileText, Send, MessageSquareReply, Clock, ShieldAlert, ArrowUpRight } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, TrackIcon, Empty } from "@/components/Bits";
import { OutlookBanner } from "@/components/OutlookConnect";

const METRICS = [
  ["runs_this_week", "Research runs · 7d", Activity],
  ["leads_reviewed", "Leads reviewed", Eye],
  ["qualified", "Qualified leads", CheckCircle2],
  ["drafts_ready", "Drafts ready", FileText],
  ["emails_sent", "Emails sent", Send],
  ["replies", "Replies", MessageSquareReply],
  ["followups_due", "Follow-ups due", Clock],
  ["suppressed", "Suppressed", ShieldAlert],
];

function Metric({ k, label, Icon, value, i }) {
  return (
    <div className="kh-card kh-rise p-5 transition-colors hover:border-primary/40" style={{ animationDelay: `${i * 40}ms` }} data-testid={`metric-${k}`}>
      <div className="flex items-center justify-between">
        <span className="kh-label">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="mt-4 font-display text-4xl tabular-nums" data-testid={`metric-${k}-value`}>{value}</div>
    </div>
  );
}

function TrackPanel({ t }) {
  const rows = [["Leads found", t.leads], ["Qualified", t.qualified], ["Drafts created", t.drafts], ["Replies", t.replies]];
  return (
    <div className="kh-card relative overflow-hidden p-6" data-testid={`track-panel-${t.id}`}>
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: t.color }} />
      <div className="flex items-center gap-2">
        <TrackIcon name={t.icon} color={t.color} />
        <h3 className="font-display text-lg font-medium">{t.name}</h3>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-y-4">
        {rows.map(([l, v]) => (
          <div key={l}><div className="text-xs text-muted-foreground">{l}</div><div className="font-mono text-xl">{v}</div></div>
        ))}
      </div>
      <div className="mt-5 flex items-end justify-between border-t pt-4">
        <span className="kh-label">Reply rate</span>
        <span className="font-display text-3xl" style={{ color: t.color }} data-testid={`track-reply-rate-${t.id}`}>{t.reply_rate}%</span>
      </div>
    </div>
  );
}

function Pipeline({ p }) {
  const order = ["new", "saved", "approved", "drafted", "sent", "replied", "dead"];
  const total = order.reduce((a, k) => a + (p[k] || 0), 0) || 1;
  return (
    <div className="kh-card p-6" data-testid="pipeline-panel">
      <div className="kh-label">Lead pipeline</div>
      <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-secondary">
        {order.map((k, i) => <div key={k} style={{ width: `${(p[k] / total) * 100}%`, opacity: 1 - i * 0.11 }} className="bg-primary" />)}
      </div>
      <div className="mt-5 grid grid-cols-4 gap-4 sm:grid-cols-7">
        {order.map((k) => (
          <div key={k}><div className="font-mono text-lg">{p[k] || 0}</div><div className="text-xs capitalize text-muted-foreground">{k}</div></div>
        ))}
      </div>
    </div>
  );
}

function Usage({ u }) {
  return (
    <div className="kh-card p-6" data-testid="usage-panel">
      <div className="flex items-center justify-between">
        <div className="kh-label">Usage / research cost</div>
        <span className={`text-xs ${u.ai_enabled ? "text-emerald-700" : "text-muted-foreground"}`} data-testid="usage-ai-state">AI {u.ai_enabled ? "on" : "off"}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div><div className="font-mono text-xl">{u.runs_total}</div><div className="text-xs text-muted-foreground">Research runs (all)</div></div>
        <div><div className="font-mono text-xl">{u.runs_30d}</div><div className="text-xs text-muted-foreground">Runs · 30d</div></div>
        <div><div className="font-mono text-xl">{u.sites_audited}</div><div className="text-xs text-muted-foreground">Sites audited</div></div>
        <div><div className="font-mono text-xl" data-testid="usage-ai-requests">{u.ai_requests}</div><div className="text-xs text-muted-foreground">AI requests</div></div>
      </div>
      <div className="mt-5 space-y-2 border-t pt-4">
        {u.providers.length === 0 && <div className="text-xs text-muted-foreground">No AI usage — core research runs free on public directories.</div>}
        {u.providers.map((p) => (
          <div key={p.provider} className="flex justify-between text-sm">
            <span className="capitalize">{p.provider}</span>
            <span className="font-mono text-muted-foreground">{p.requests} req · ${p.spend.toFixed(4)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-muted-foreground" data-testid="overview-loading">Loading overview…</div>;
  return (
    <>
      <PageHeader eyebrow={`Daily batch · ${d.today.used} of ${d.today.cap} used`} title="Overview">
        <Link to="/prospects" data-testid="overview-new-search-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90">
          New search <ArrowUpRight className="h-4 w-4" />
        </Link>
      </PageHeader>
      <OutlookBanner />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {METRICS.map(([k, l, I], i) => <Metric key={k} k={k} label={l} Icon={I} value={d.metrics[k]} i={i} />)}
      </div>
      <div className="grid gap-4 md:grid-cols-3">{d.tracks.map((t) => <TrackPanel key={t.id} t={t} />)}</div>
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-4">
          <Pipeline p={d.pipeline} />
          <div className="kh-card p-6" data-testid="recent-drafts-panel">
            <div className="flex items-center justify-between"><div className="kh-label">Recent outbox</div><Link to="/drafts" className="text-xs text-primary hover:underline">View all</Link></div>
            {d.recent_drafts.length === 0 ? <Empty>No drafts yet — approve a few qualified leads to start.</Empty> : (
              <div className="mt-3 divide-y">
                {d.recent_drafts.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-3 text-sm">
                    <div><div className="font-medium">{r.lead_name}</div><div className="text-xs text-muted-foreground">{r.track_name} · {fmtDate(r.created_at)}</div></div>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <Usage u={d.usage} />
      </div>
    </>
  );
}
